/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import ViewportEvent, {eventSx} from "./ViewportEvent";
import {Employee, GoogleEvent} from "../interfaces/Global";
import {getEventType} from "./event-types";
import {formatDistance} from "date-fns";
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader, Stack,
	Typography, useTheme
} from "@mui/material";
import ModelView, {AttributeConfig} from "../components/ModelView";
import Avatar from "../components/Avatar";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {format} from "../dates-helper";

export interface GoogleViewportEventProps {
	viewportRange: Interval
	rangeResolution: number
	employee: Employee
	event: GoogleEvent
}

export default function GoogleViewportEvent(props: GoogleViewportEventProps) {

	const eventType = getEventType(props.event.typeId);
	const theme = useTheme();

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={props.employee.timeZone}
			event={{
				title: eventType?.name || "Calendar Event",
				typeId: eventType?.id || "AL",
				startDatetime: new Date(props.event.startDatetime),
				endDatetime: new Date(props.event.endDatetime)
			}}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			details={
				<EventDetails
					event={props.event}
					employee={props.employee}
				/>
			}
			viewportIcon={eventType?.icon}
			getViewportLabel={(width) => {
				if (!eventType) return null;
				if (width <= 40) {
					return null;
				} else if (width <= 80) {
					return eventType.id;
				}
				return props.event.summary;
			}}
			sx={{
				...eventSx(theme, eventType?.parent?.id?.toLowerCase(), !props.event.isConfirmed),
				...eventSx(theme, props.event.typeId?.toLowerCase(), !props.event.isConfirmed),
				zIndex: 25
			}}
		/>
	);

}

interface EventTooltipProps {
	event: GoogleEvent
	employee: Employee
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	const eventType = getEventType(event.typeId);

	return (
		<Stack>
			<Stack>{employee.displayName}</Stack>
			<Typography>{event.summary}</Typography>
			<Stack>Google Calendar Event</Stack>
			<Stack>{eventType?.name}</Stack>
			<Stack>From: {format(event.startDatetime, 'PPPP p')}</Stack>
			<Stack>To: {format(event.endDatetime, 'PPPP p')}</Stack>
			<Stack>{formatDistance(event.endDatetime, event.startDatetime)}</Stack>
			{!event.isConfirmed && <Stack>Not Confirmed</Stack>}
		</Stack>
	)

}

interface EventDetailsProps {
	event: GoogleEvent
	employee: Employee
}

function EventDetails({ event, employee }: EventDetailsProps) {

	const eventType = getEventType(event.typeId);

	const attributes: AttributeConfig<GoogleEvent>[] = [
		{ label: "Source", value: "Google Calendar" },
		{ label: "Type", value: eventType?.name },
		{ label: "Category", value: eventType?.parent?.name },
		{ attribute: "creationDate", format: "datetime" },
		"createdBy",
		{ attribute: "isConfirmed", label: "Confirmed", format: "boolean" },
		{ attribute: "startDatetime", format: "datetime", label: "Start Date" },
		{ attribute: "endDatetime", format: "datetime", label: "End Date" },
	];

	return (
		<Card>
			<CardHeader
				avatar={<Avatar id={employee.id as string} />}
				title={event.summary}
				subheader={employee.displayName}
			/>
			<CardContent>
				<ModelView
					attributes={attributes}
					model={event}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 500,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
			<CardActions disableSpacing>
				<Button
					size="small"
					href={event.htmlLink || "#"}
					target={"_blank"}
					startIcon={<OpenInNewIcon />}
				>
					Open Details
				</Button>
			</CardActions>
		</Card>
	);

}